import { useCallback } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  IconBack,
  InformationalScreen,
  LoadingModal,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { type FormikHelpers, useFormik } from 'formik';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { FormContainer, MainContainer, PhoneInput } from '~/components/utility';
import { ApiStatus, ErrorCode } from '~/constants';
import { useApiError, useAuth } from '~/hooks';
import { type RootState } from '~/reducers';
import { updatePhone } from '~/services/patient';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';

const mapStateToProps = ({
  auth: { account_id },
  env: { osType, osVersion, appVersion, deviceId },
}: RootState) => ({
  accountId: account_id,
  deviceId,
  osType,
  osVersion,
  appVersion,
});

const initialValuesFormik = {
  phone: '',
  repeatPhone: '',
};

type UpdatePhoneFormValues = typeof initialValuesFormik;

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const testID = 'update-phone';

const UpdatePhone = ({
  accountId,
  osType,
  osVersion,
  appVersion,
  deviceId,
}: Props) => {
  const validationSchema = z
    .object({
      phone: z
        .string({
          error: i18n.t(
            'UpdatePhone.content.updatePhoneForm.formField.phone.errors.required',
          ),
        })
        .trim()
        .min(1, {
          error: i18n.t(
            'UpdatePhone.content.updatePhoneForm.formField.phone.errors.required',
          ),
        })
        .refine(
          (value) => parsePhoneNumberFromString(value)?.isPossible() ?? false,
          {
            error: i18n.t(
              'UpdatePhone.content.updatePhoneForm.formField.phone.errors.phoneNumberValid',
            ),
          },
        ),
      repeatPhone: z
        .string({
          error: i18n.t(
            'UpdatePhone.content.updatePhoneForm.formField.phone.errors.required',
          ),
        })
        .trim()
        .min(1, {
          error: i18n.t(
            'UpdatePhone.content.updatePhoneForm.formField.phone.errors.required',
          ),
        }),
    })
    .refine((data) => data.phone === data.repeatPhone, {
      path: ['repeatPhone'],
      error: i18n.t(
        'UpdatePhone.content.updatePhoneForm.formField.repeatPhone.errors.phoneMustMatch',
      ),
    });

  const handleClickBack = useCallback(() => {
    mediator.publish('router:navigate', '/account-information');
  }, []);

  const { showApiErrorModal, showNetworkErrorModal, showCustomError } =
    useApiError();

  const { handleLoginError, showHcpError } = useAuth('changepassword');

  const handleApiRequestError = useCallback(
    (
      error: AxiosError<ApiErrorData>,
      resetForm: () => void,
      setFieldValue: FormikHelpers<UpdatePhoneFormValues>['setFieldValue'],
      setTouched: FormikHelpers<UpdatePhoneFormValues>['setTouched'],
    ) => {
      switch (error.status) {
        case ApiStatus.FORBIDDEN: {
          const code = error.response?.data.code;

          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();
          }

          break;
        }

        case ApiStatus.UNAUTHORIZED:
          handleLoginError(error, resetForm, () => {
            void setFieldValue('phone', '');
            void setFieldValue('repeatPhone', '');
          });
          break;

        case ApiStatus.BAD_REQUEST: {
          void setFieldValue('phone', '');
          void setFieldValue('repeatPhone', '');
          void setTouched({});
          resetForm();

          const errorCode = error.response?.data.code;

          if (errorCode === ApiStatus.PHONE_CODE_INVALID) {
            showCustomError(i18n.t('Global.modals.invalidPhoneCode.body'));
          } else {
            showCustomError(error.response?.data.details ?? '');
          }

          break;
        }

        case ApiStatus.TOO_MANY_REQUESTS: {
          resetForm();
          break;
        }

        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [
      handleLoginError,
      showApiErrorModal,
      showHcpError,
      showCustomError,
      showNetworkErrorModal,
    ],
  );

  const onSubmitForm = useCallback(
    async (
      values: UpdatePhoneFormValues,
      {
        setSubmitting,
        resetForm,
        setFieldValue,
        setTouched,
      }: FormikHelpers<UpdatePhoneFormValues>,
    ) => {
      try {
        await updatePhone({
          updatePhoneData: {
            userID: accountId,
            phone: values.phone,
          },
          osType,
          osVersion,
          appVersion,
          deviceId,
        });

        notifyApp('adc-webview:update-phone');
        mediator.publish('router:navigate', '/account-information');
      } catch (error) {
        handleApiRequestError(
          error as AxiosError<ApiErrorData>,
          resetForm,
          setFieldValue,
          setTouched,
        );
      }

      setSubmitting(false);
    },
    [accountId, appVersion, osType, osVersion, deviceId, handleApiRequestError],
  );

  const {
    handleSubmit,
    handleBlur,
    handleChange,
    isValid,
    isSubmitting,
    errors,
    touched,
    values,
    dirty,
  } = useFormik({
    initialValues: initialValuesFormik,
    onSubmit: onSubmitForm,
    validateOnBlur: true,
    validationSchema: toFormikValidationSchema(validationSchema),
  });

  return (
    <MainContainer testID={testID}>
      {isSubmitting && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({
            testID,
            suffix: ['loading', 'modal'],
          })}
        />
      )}

      <InformationalScreen testID={buildTestId({ testID, suffix: 'screen' })}>
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
          topNavigationProps={{
            title: i18n.t('UpdatePhone.title'),
            buttonStart: {
              element: <IconBack testID="nav-back" />,
              label: 'Go Back',
              onPress: handleClickBack,
            },
            testID: 'nav-back',
          }}
        >
          <InformationalScreen.Content>
            <FormContainer hasHiddenUserInput>
              <PhoneInput
                aria-label={i18n.t(
                  'UpdatePhone.content.updatePhoneForm.formField.phone.label',
                )}
                placeholder={i18n.t(
                  'UpdatePhone.content.updatePhoneForm.formField.phone.placeholder',
                )}
                label={i18n.t(
                  'UpdatePhone.content.updatePhoneForm.formField.phone.label',
                )}
                errorMessage={errors.phone}
                isInvalid={Boolean(errors.phone && touched.phone)}
                testID={buildTestId({ testID, suffix: ['phone', 'input'] })}
                value={values.phone}
                onChangeText={handleChange('phone')}
                onBlur={handleBlur('phone')}
              />
              <PhoneInput
                aria-label={i18n.t(
                  'UpdatePhone.content.updatePhoneForm.formField.repeatPhone.label',
                )}
                placeholder={i18n.t(
                  'UpdatePhone.content.updatePhoneForm.formField.repeatPhone.placeholder',
                )}
                label={i18n.t(
                  'UpdatePhone.content.updatePhoneForm.formField.repeatPhone.label',
                )}
                errorMessage={errors.repeatPhone}
                isInvalid={Boolean(errors.repeatPhone && touched.repeatPhone)}
                testID={buildTestId({
                  testID,
                  suffix: ['repeat', 'phone', 'input'],
                })}
                value={values.repeatPhone}
                onChangeText={handleChange('repeatPhone')}
                onBlur={handleBlur('repeatPhone')}
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button
              label={i18n.t('Global.microcopy.common.save')}
              disabled={!isValid || isSubmitting || !dirty}
              onPress={() => {
                handleSubmit();
              }}
              testID={buildTestId({ testID, suffix: ['save', 'button'] })}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(UpdatePhone);
